import {
  CircularProgress,
  IconButton,
  InputLabel,
  Typography,
} from "@mui/material";
import { Bolt, Close, SystemUpdateAlt } from "@mui/icons-material";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { t } from "ttag";
import fieldStyles from "../../widgets/AdvancedInputField/advancedInputField.module.css";
import FirmwareReleaseNotesHelp from "../Firmware/FirmwareReleaseNotesHelp";
import useStyles from "./DeviceManagement.style";

/** Set true to show the force-upgrade (Bolt) control again. */
const SHOW_FORCE_FIRMWARE = false;

interface EdgeFirmwareVersionRowProps {
  formikProps: any;
  device: any;
  isFirmwareUpdating: boolean;
  currentFwStage: string;
  currentFwStatus: string;
  canForceFirmware: boolean;
  upgradeActionId: string | number | null;
  isForcingFirmware: boolean;
  isCancellingFirmware: boolean;
  currentFirmwareCatalog: any;
  onForce: () => void;
  onCancel: () => void;
  onOpenUpgrade: () => void;
  onOpenHistory?: () => void;
}

const EdgeFirmwareVersionRow = ({
  formikProps,
  device,
  isFirmwareUpdating,
  currentFwStage,
  currentFwStatus,
  canForceFirmware,
  upgradeActionId,
  isForcingFirmware,
  isCancellingFirmware,
  currentFirmwareCatalog,
  onForce,
  onCancel,
  onOpenUpgrade,
  onOpenHistory,
}: EdgeFirmwareVersionRowProps) => {
  const styles: any = useStyles();
  const firmwareVersion =
    formikProps?.values?.firmwareVersion ||
    device?.firmwareVersion ||
    "";

  const rowRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [showAccessory, setShowAccessory] = useState(true);

  const updateAccessoryVisibility = () => {
    const row = rowRef.current;
    const label = labelRef.current;
    const measure = measureRef.current;
    if (!row || !label || !measure) {
      return;
    }
    const available = row.clientWidth - label.offsetWidth - 8;
    setShowAccessory(available >= measure.offsetWidth);
  };

  const isConnected = !!device?.isConnected;

  useLayoutEffect(() => {
    updateAccessoryVisibility();
  }, [firmwareVersion, isFirmwareUpdating, currentFwStage, currentFwStatus, isConnected]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => updateAccessoryVisibility());
    observer.observe(row);
    window.addEventListener("resize", updateAccessoryVisibility);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateAccessoryVisibility);
    };
  }, [firmwareVersion, isFirmwareUpdating, isConnected]);

  const helpBtn = (
    <FirmwareReleaseNotesHelp
      releaseNotes={currentFirmwareCatalog?.releaseNotes}
      versionLabel={currentFirmwareCatalog?.version || firmwareVersion}
      className={styles.firmwareReleaseNotesHelpBtn}
    />
  );

  const statusText =
    isFirmwareUpdating && !!(currentFwStage || currentFwStatus) ? (
      <Typography
        className={styles.firmwareUpdateState}
        title={currentFwStage || currentFwStatus}
      >
        ({currentFwStage || currentFwStatus})
      </Typography>
    ) : null;

  const actions = (
    <div className={styles.firmwareVersionActions}>
      {isFirmwareUpdating ? (
        <>
          <span
            title={t`Firmware update in progress`}
            className={styles.firmwareUpdatingSpinnerWrap}
          >
            <CircularProgress
              className={styles.firmwareUpdatingSpinner}
              size={18}
              thickness={4}
            />
          </span>
          {SHOW_FORCE_FIRMWARE &&
            currentFwStatus === "PENDING" &&
            canForceFirmware &&
            (upgradeActionId || device?.fmuAction) && (
              <IconButton
                className={styles.firmwareForceBtn}
                onClick={onForce}
                title={t`Force update`}
                size="small"
                disabled={isForcingFirmware}
              >
                {isForcingFirmware ? <CircularProgress size={18} /> : <Bolt />}
              </IconButton>
            )}
          <IconButton
            className={styles.firmwareCancelBtn}
            onClick={onCancel}
            title={t`Cancel firmware upgrade`}
            size="small"
            disabled={isCancellingFirmware}
          >
            {isCancellingFirmware ? <CircularProgress size={18} /> : <Close />}
          </IconButton>
        </>
      ) : (
        isConnected && (
          <IconButton
            className={styles.firmwareUpgradeBtn}
            onClick={onOpenUpgrade}
            title={t`Update Firmware`}
            size="small"
          >
            <SystemUpdateAlt />
          </IconButton>
        )
      )}
    </div>
  );

  return (
    <div className={`${fieldStyles.dataRow} ${styles.firmwareVersionRow}`}>
      <div
        ref={rowRef}
        className={`${fieldStyles.flexBox} ${styles.firmwareVersionInner}`}
      >
        <InputLabel ref={labelRef} className={fieldStyles.siteDetailsLabel}>
          {t`Firmware Version`}
        </InputLabel>

        {/* Version + ? stay glued together; do not grow */}
        <div className={styles.firmwareVersionInline}>
          <span className={styles.firmwareVersionText}>{firmwareVersion}</span>
          {showAccessory && helpBtn}
          {showAccessory && statusText}
        </div>

        {/* Upgrade / history stay on the far right */}
        {showAccessory && actions}
      </div>

      <div ref={measureRef} className={styles.firmwareVersionMeasure} aria-hidden>
        {helpBtn}
        {statusText}
        {actions}
      </div>
    </div>
  );
};

export default EdgeFirmwareVersionRow;
